import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Anaheim", cities: "Anaheim Resort, Platinum Triangle, Anaheim Hills, West Anaheim" },
    { zone: "Norte de OC", cities: "Fullerton, Placentia, Brea" },
    { zone: "Este", cities: "Orange, Villa Park" },
    { zone: "Oeste", cities: "Buena Park, Stanton, Cypress" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Vecindarios que Servimos en Anaheim</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Anaheim, Fullerton, Orange y ciudades vecinas.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar en Anaheim</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const AnaheimSpanishPage = () => (
  <LocationContext.Provider value={{ slug: "anaheim", name: "Anaheim", type: "city", regionSlug: "orange-county" }}>
    <SEO
      title="Limpieza del Hogar en Anaheim, CA — Uplevel Cleaning"
      description="Servicio profesional de limpieza del hogar en Anaheim, CA. Precios planos, reserva en 60 segundos, garantía del 200%. Limpieza estándar, profunda y de mudanza."
      keywords="limpieza anaheim, maid service anaheim español, servicio de limpieza anaheim ca, empleada doméstica anaheim"
      url="https://uplevelcleaning.com/es/anaheim"
      locationName="Anaheim"
      regionSlug="orange-county"
      lang="es"
      includeLocalBusiness={true}
      includeFAQ={true}
      breadcrumbs={[{ name: "Inicio", url: "/" }, { name: "Orange County", url: "/es/orange-county" }, { name: "Anaheim", url: "/es/anaheim" }]}
      reviews={[
        { author: "Laura M.", rating: 5, reviewBody: "Excelente servicio en Anaheim. El equipo fue muy profesional y dejó mi casa reluciente. Ya reservé mi limpieza quincenal.", datePublished: "2026-03-12" },
        { author: "Javier T.", rating: 5, reviewBody: "Muy fácil reservar en línea y el precio es exactamente lo que te dicen. Muy satisfecho con la limpieza profunda.", datePublished: "2026-02-18" },
      ]}
    />
    <div className="flex min-h-screen flex-col" lang="es">
      <StickyBanner />
      <Navbar />
      <main className="flex-1">
        <SpanishHero cityName="Anaheim" regionSlug="orange-county" />
        <div className="bg-primary/5 border-b border-primary/10 py-4">
          <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
            {["Servicio Local en Anaheim", "Profesionales Verificados", "Garantía del 200%", "Reserva en 60 Segundos"].map((item, i) => (
              <span key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {item}</span>
            ))}
          </div>
        </div>
        <SpanishWhyUs cityName="Anaheim" />
        <NearbyAreas />
        <SpanishServices />

        <FrequencyDiscounts />
        <PricingCalculator />
        <SpanishTestimonials region="orange-county" />
        <Guarantee />
        <SpanishFAQ cityName="Anaheim" />
        <InternalLinks />
        <SpanishFinalCTA cityName="Anaheim" />
      </main>
      <Footer />
    </div>
  </LocationContext.Provider>
);

export default AnaheimSpanishPage;
